<template>
  <div class="d-flex flex-column-fluid invoices-page">
    <div class="container">
      <div class="page-header mb-4">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Late Fees</h1>
            <p class="subtitle">Overdue factoring invoices billed after they are fully repaid</p>
          </div>
        </div>
      </div>

      <div class="filters-section mb-3">
        <div class="filters-bar">
          <div class="filters-controls">
            <div class="filter-group search-group">
              <i class="las la-search filter-group-icon"></i>
              <input
                type="text"
                class="filter-input search-input"
                v-model="searchTerm"
                @input="handleSearchInput"
                placeholder="Search by reference or business..."
              />
            </div>
            <div class="filter-group business-group">
              <div class="filter-dropdown-wrapper">
                <button class="filter-dropdown-toggle" @click="toggleBusinessDropdown">
                  <i class="las la-building filter-toggle-icon"></i>
                  <span v-if="selectedBusinesses.length === 0" class="placeholder-text">All businesses</span>
                  <span v-else class="selected-text">
                    {{ selectedBusinesses.length === 1 ? selectedBusinesses[0].name : `${selectedBusinesses.length} businesses` }}
                  </span>
                  <i class="las la-angle-down dropdown-chevron"></i>
                </button>
                <div v-if="showBusinessDropdown" class="filter-dropdown-menu">
                  <div class="dropdown-search">
                    <i class="las la-search search-icon"></i>
                    <input
                      type="text"
                      class="search-input"
                      v-model="businessSearchTerm"
                      @input="searchBusinesses"
                      placeholder="Search businesses..."
                      @click.stop
                    />
                  </div>
                  <div class="dropdown-options">
                    <label
                      v-for="business in filteredBusinesses"
                      :key="business.id"
                      class="dropdown-option"
                    >
                      <input
                        type="checkbox"
                        :checked="selectedBusinesses.some(b => b.id === business.id)"
                        @change="toggleBusinessSelection(business)"
                      />
                      <span class="option-label">{{ business.name }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="clear-all-filters-btn"
            @click="clearAllFilters"
            :disabled="!hasActiveFilters"
          >
            <i class="las la-times"></i>
            Clear filters
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading late fees...</div>
      </div>
      <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
      <div v-else class="invoices-table-container">
        <financing-table
          :columns="tableColumns"
          :data="fees"
          :grid-template="gridTemplate"
          :total-rows="totalFees"
          :current-page="currentPage"
          :page-size="pageSize"
          :table-name="'late-fees'"
          :empty-title="'No late fees found'"
          :empty-subtitle="'Late fee invoices appear automatically when an overdue factoring invoice is fully repaid'"
          :empty-icon="'las la-file-invoice-dollar'"
          :on-row-click="openFeeDetail"
          @page-change="onPageChanged"
          @action="handleTableAction"
        />
      </div>
    </div>

    <pdf-viewer
      v-if="showPDF"
      :url="pdfUrl"
      :name="pdfName"
      :showHeader="true"
      :forceExpansion="true"
      :enableOverlayEffect="true"
      @closePDF="closePDFViewer"
    />
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import CompaniesService from "@/services/companies.service";
import FinancingTable from "@/components/UI/FinancingTable.vue";
import PdfViewer from "@/components/pdfViewer.vue";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "LateFeesPage",
  components: { FinancingTable, PdfViewer },
  data() {
    return {
      loading: true,
      error: null,
      searchTerm: "",
      searchDebounceTimer: null,
      selectedBusinesses: [],
      filteredBusinesses: [],
      businessSearchTerm: "",
      showBusinessDropdown: false,
      fees: [],
      currentPage: 1,
      pageSize: 25,
      totalFees: 0,
      showPDF: false,
      pdfUrl: null,
      pdfName: "",
      gridTemplate: "140px 180px 140px 120px 120px 90px 140px 90px 80px",
      tableColumns: [
        { header: "Reference", field: "reference", type: "text", width: "140px" },
        { header: "Business", field: "business_name", type: "text", width: "180px" },
        { header: "Factoring invoice", field: "invoice_reference", type: "text", width: "140px" },
        { header: "Due date", field: "period_start", type: "text", width: "120px" },
        { header: "Paid date", field: "period_end", type: "text", width: "120px" },
        { header: "Rate", field: "rate_display", type: "text", width: "90px", align: "right" },
        { header: "Fee", field: "fee_amount", type: "currency", currencyField: "currency", width: "140px", align: "right" },
        { header: "Status", field: "status", type: "text", width: "90px" },
        { header: "PDF", field: "pdf_url", type: "action", action: "download", icon: "las la-file-download", tooltip: "View invoice", width: "80px", align: "center" }
      ]
    };
  },
  computed: {
    hasActiveFilters() {
      return this.selectedBusinesses.length > 0 || !!(this.searchTerm && this.searchTerm.trim());
    }
  },
  mounted() {
    this.fetchBusinesses();
    this.fetchFees();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    this.closePDFViewer();
  },
  methods: {
    async fetchBusinesses() {
      try {
        const response = await CompaniesService.query({
          page: 1,
          page_size: 50,
          financing_enabled: true,
          list_view: "True",
          search: this.businessSearchTerm || undefined
        });
        this.filteredBusinesses = (response.data || []).map(business => ({
          id: business.id,
          name: business.name
        }));
      } catch (error) {
        console.error("Error fetching businesses:", error);
      }
    },
    searchBusinesses() {
      this.fetchBusinesses();
    },
    async fetchFees(page = 1) {
      this.loading = true;
      this.error = null;
      try {
        const params = { page, page_size: this.pageSize };
        if (this.searchTerm && this.searchTerm.trim()) {
          params.search = this.searchTerm.trim();
        }
        if (this.selectedBusinesses.length > 0) {
          params.business_id = this.selectedBusinesses.map(b => b.id);
        }
        const response = await FinancingService.getLateFees(params);
        const rows = response.data || [];
        this.fees = rows.map(row => ({
          ...row,
          rate_display: row.rate != null ? `${row.rate}%` : "-"
        }));
        this.totalFees = response.count || 0;
        this.currentPage = page;
      } catch (error) {
        this.error = formatErrorMessage(error) || "Failed to load late fees.";
        this.fees = [];
        this.totalFees = 0;
      } finally {
        this.loading = false;
      }
    },
    handleSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => this.fetchFees(1), 400);
    },
    toggleBusinessDropdown() {
      this.showBusinessDropdown = !this.showBusinessDropdown;
    },
    toggleBusinessSelection(business) {
      const exists = this.selectedBusinesses.some(b => b.id === business.id);
      this.selectedBusinesses = exists
        ? this.selectedBusinesses.filter(b => b.id !== business.id)
        : [...this.selectedBusinesses, business];
      this.fetchFees(1);
    },
    clearAllFilters() {
      this.searchTerm = "";
      this.selectedBusinesses = [];
      this.fetchFees(1);
    },
    handleClickOutside(event) {
      if (!event.target.closest(".filter-dropdown-wrapper")) {
        this.showBusinessDropdown = false;
      }
    },
    onPageChanged(page) {
      this.fetchFees(page);
    },
    openFeeDetail(row) {
      if (!row || !row.id) return;
      this.$router.push({
        name: "financing-late-fee-detail",
        params: { fee_id: row.id },
        query: { business_id: row.business_id },
      });
    },
    async handleTableAction({ action, row }) {
      if (action !== "download") return;
      try {
        const blob = await FinancingService.getLateFeePdf(row.id);
        this.pdfUrl = URL.createObjectURL(blob);
        this.pdfName = `${row.reference || "late-fee"}.pdf`;
        this.showPDF = true;
      } catch (error) {
        this.$showAlert("Failed to load PDF", "warning");
      }
    },
    closePDFViewer() {
      this.showPDF = false;
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.pdfName = "";
    }
  }
};
</script>

<style lang="scss" scoped>
.invoices-page {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding-bottom: 40px;
}
.page-header .header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
}
.subtitle {
  color: #6c757d;
  margin: 4px 0 0;
}
.filters-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.filters-controls {
  display: flex;
  gap: 12px;
  flex: 1;
}
.filter-group {
  position: relative;
}
.search-group {
  flex: 1;
  max-width: 360px;
}
.filter-input,
.search-input {
  width: 100%;
  height: 36px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 0 12px 0 32px;
}
.filter-group-icon,
.search-icon {
  position: absolute;
  left: 10px;
  top: 10px;
  color: #6c757d;
}
.filter-dropdown-toggle {
  height: 36px;
  border: 1px solid #ced4da;
  background: #fff;
  border-radius: 4px;
  padding: 0 12px;
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-dropdown-menu {
  position: absolute;
  z-index: 20;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  width: 280px;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.dropdown-search {
  position: relative;
  padding: 8px;
}
.dropdown-options {
  max-height: 240px;
  overflow-y: auto;
}
.dropdown-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin: 0;
}
.clear-all-filters-btn {
  border: none;
  background: transparent;
  color: #6c757d;
}
.loading-state,
.invoices-table-container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
}
.loading-state {
  text-align: center;
}
</style>
